import React, { useState } from "react";
import { Select, Button } from "antd";

interface ImageTabProps {
    customerCode?: string;
}

export default function ImageTab({ customerCode = "KH-19950" }: ImageTabProps) {
    const [beforePhase, setBeforePhase] = useState("khamcoso");
    const [afterPhase, setAfterPhase] = useState("t24h");
    const [angleFilter, setAngleFilter] = useState("Thẳng");

    // State quản lý hiển thị kết quả so sánh khi bấm nút "So sánh"
    const [isComparing, setIsComparing] = useState(true);
    const [compareData, setCompareData] = useState({
        leftLabel: "Khám tại cơ sở",
        leftTime: "20/12/2025",
        rightLabel: "T+24h",
        rightTime: "21/12/2025",
        angle: "Thẳng"
    });

    const handleCompare = () => {
        // Ánh xạ tên hiển thị từ giá trị select
        const labelMap: Record<string, string> = {
            tuvandau: "Trước tư vấn",
            khamcoso: "Khám tại cơ sở",
            ngaysaulam: "Ngay sau làm",
            t24h: "T+24h"
        };

        const timeMap: Record<string, string> = {
            tuvandau: "19/12/2025",
            khamcoso: "20/12/2025",
            ngaysaulam: "20/12/2025",
            t24h: "21/12/2025"
        };

        setCompareData({
            leftLabel: labelMap[beforePhase] || beforePhase,
            leftTime: timeMap[beforePhase] || "",
            rightLabel: labelMap[afterPhase] || afterPhase,
            rightTime: timeMap[afterPhase] || "",
            angle: angleFilter
        });
        setIsComparing(true);
    };

    // Dữ liệu mẫu các nhóm ảnh theo từng mốc thời gian chuẩn giao diện
    const imageTimeline = [
        {
            title: "Trước tư vấn",
            subTitle: "Khách gửi Zalo",
            uploader: "Telesales NV-02",
            images: [{ label: "Thẳng", code: customerCode }]
        },
        {
            title: "Khám tại cơ sở",
            subTitle: "Nhân viên chụp",
            uploader: "KTV-03",
            images: [
                { label: "Thẳng", code: customerCode },
                { label: "Nghiêng trái", code: customerCode },
                { label: "Nghiêng phải", code: customerCode }
            ]
        },
        {
            title: "Ngay sau làm",
            subTitle: "20/12/2025 · Nhân viên chụp",
            uploader: "KTV-03",
            images: [{ label: "Thẳng", code: customerCode }]
        },
        {
            title: "T+24h",
            subTitle: "21/12/2025 · Khách gửi Zalo",
            uploader: "CSKH NV-07",
            images: [{ label: "Thẳng", code: customerCode }]
        },
        { title: "T+3", subTitle: "23/12/2025", hasImage: false },
        { title: "T+7", subTitle: "27/12/2025", hasImage: false },
        { title: "T+30", subTitle: "19/01/2026", hasImage: false },
        { title: "T+90", subTitle: "20/03/2026", hasImage: false },
        { title: "Bất thường", subTitle: "", hasImage: false }
    ];

    return (
        <div className="space-y-3 pt-1 text-xs animate-fade-in text-gray-800 dark:text-gray-200">
            {/* Dòng quy tắc đầu tab */}
            <div className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Dịch vụ gắn: <strong>Phác đồ K·Reskin · 20/12/2025</strong> · không tải xuống, ảnh đóng dấu mã khách, mỗi lần mở ảnh ghi nhật ký · Đồng ý dùng cho truyền thông: <strong>Không</strong> (mặc định) <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.5 rounded font-bold cursor-pointer ml-1">[?] mẫu đồng ý riêng</span>
            </div>

            {/* Thanh công cụ So sánh trước / sau */}
            <div className="bg-slate-100/80 dark:bg-[#252525] p-2.5 rounded border border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
                <span className="font-bold text-gray-800 dark:text-gray-200">So sánh trước / sau</span>
                
                <Select
                    size="small"
                    value={beforePhase}
                    onChange={(val) => setBeforePhase(val)}
                    className="w-36 text-xs"
                    options={[
                        { label: "Trước tư vấn", value: "tuvandau" },
                        { label: "Khám tại cơ sở", value: "khamcoso" },
                        { label: "Ngay sau làm", value: "ngaysaulam" },
                    ]}
                />

                <span className="text-gray-500">với</span>

                <Select
                    size="small"
                    value={afterPhase}
                    onChange={(val) => setAfterPhase(val)}
                    className="w-36 text-xs"
                    options={[
                        { label: "Khám tại cơ sở", value: "khamcoso" },
                        { label: "Ngay sau làm", value: "ngaysaulam" },
                        { label: "T+24h", value: "t24h" },
                    ]}
                />

                <span className="text-gray-500">· góc</span>

                <Select
                    size="small"
                    value={angleFilter}
                    onChange={(val) => setAngleFilter(val)}
                    className="w-28 text-xs"
                    options={[
                        { label: "Thẳng", value: "Thẳng" },
                        { label: "Nghiêng trái", value: "Nghiêng trái" },
                        { label: "Nghiêng phải", value: "Nghiêng phải" },
                    ]}
                />

                <Button 
                    size="small"
                    type="primary"
                    onClick={handleCompare}
                    className="text-xs font-bold !bg-[#1d4c7d]"
                >
                    So sánh
                </Button>
            </div>

            {/* Khung hiển thị kết quả so sánh 2 ảnh song song giống hình mẫu */}
            {isComparing && (
                <div className="bg-slate-50 dark:bg-[#141414] p-3 rounded border border-blue-200 dark:border-blue-900 flex flex-wrap gap-4 items-center">
                    {/* Ảnh bên trái */}
                    <div className="space-y-1">
                        <div className="relative w-44 h-44 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 p-2 flex flex-col justify-between shadow-sm">
                            <div className="text-[11px] text-gray-400 font-mono text-right">{customerCode}</div>
                            <div className="bg-[#1d4c7d] text-white text-[10px] px-2 py-0.5 rounded font-semibold w-max shadow">
                                {compareData.angle}
                            </div>
                        </div>
                        <div className="text-center font-medium text-gray-800 dark:text-gray-200">
                            {compareData.leftLabel} <span className="text-gray-400 font-normal">· {compareData.leftTime}</span>
                        </div>
                    </div>

                    {/* Ảnh bên phải */}
                    <div className="space-y-1">
                        <div className="relative w-44 h-44 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 p-2 flex flex-col justify-between shadow-sm">
                            <div className="text-[11px] text-gray-400 font-mono text-right">{customerCode}</div>
                            <div className="bg-[#1d4c7d] text-white text-[10px] px-2 py-0.5 rounded font-semibold w-max shadow">
                                {compareData.angle}
                            </div>
                        </div>
                        <div className="text-center font-medium text-gray-800 dark:text-gray-200">
                            {compareData.rightLabel} <span className="text-gray-400 font-normal">· {compareData.rightTime}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Danh sách các mốc thời gian ảnh */}
            <div className="space-y-4 pt-1">
                {imageTimeline.map((item, idx) => (
                    <div key={idx} className="border-l-2 border-[#1d4c7d] pl-3 space-y-1.5">
                        <div className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                            <span className="text-[#1d4c7d] dark:text-blue-400">{item.title}</span>
                            {item.subTitle && <span className="text-gray-500 font-normal">· {item.subTitle}</span>}
                        </div>

                        {item.hasImage !== false ? (
                            <div>
                                <div className="flex flex-wrap gap-2.5 py-1">
                                    {item.images?.map((img, imgIdx) => (
                                        <div 
                                            key={imgIdx} 
                                            className="relative w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 overflow-hidden flex flex-col justify-between p-1.5 cursor-pointer hover:border-[#1d4c7d] transition-all shadow-sm group"
                                        >
                                            <div className="text-[10px] text-gray-400 font-mono text-right w-full">
                                                {img.code}
                                            </div>
                                            <div className="bg-[#1d4c7d] text-white text-[10px] px-1.5 py-0.5 rounded font-semibold w-max shadow">
                                                {img.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {item.uploader && (
                                    <div className="text-[11px] text-gray-500 italic pt-0.5">
                                        Người tải: {item.uploader}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 py-1">
                                <div className="w-24 h-16 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded flex items-center justify-center text-gray-400 text-xs font-medium bg-gray-50/50 dark:bg-[#252525]">
                                    Chưa có ảnh
                                </div>
                                <Button 
                                    size="small"
                                    onClick={() => alert(`Đã gửi yêu cầu Xin ảnh mốc ${item.title}!`)}
                                    className="text-xs border-gray-300 text-gray-700 font-medium"
                                >
                                    Xin ảnh
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Dòng thông báo cuối tab */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 leading-relaxed">
                Góc chụp: Newhair trước / đỉnh / 2 bên / sau - mặt thẳng / nghiêng trái / nghiêng phải <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.2 rounded font-bold cursor-pointer">[?] Khối y khoa chốt</span> · Ảnh khám do bác sĩ chụp: CMS chỉ hiện đường dẫn sang hồ sơ bệnh án <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.2 rounded font-bold cursor-pointer">[?]</span>
            </div>
        </div>
    );
}